// src/components/PumpfunCreationForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Rocket, 
  TrendingUp, 
  AlertCircle, 
  Info, 
  Loader2, 
  X, 
  Image as ImageIcon,
  ExternalLink,
  Shield,
  Zap,
  DollarSign,
  Package,
  Check,
  CheckCircle,
  Lock,
  Unlock
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import pumpfunService from '../services/pumpfun.service';
import { bundlerService } from '../services/frontend-bundler/bundler.service';
import { apiEndpoints } from '../services/api.service';
import Modal from './Modal';

interface PumpfunFormData {
  name: string;
  symbol: string;
  description: string;
  imageUrl: string;
  website: string;
  twitter: string;
  telegram: string;
  discord: string;
}

interface LaunchSettings {
  pumpfunInitialBuy: number;  // Main wallet buy on pump.fun
  bundleBuyAmount: number;     // Each bundle wallet buy amount
  slippage: number;
  priorityFee: number;
  useJitoBundle: boolean;
  jitoTip: number;
}

const PumpfunCreationForm: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<PumpfunFormData>({
    name: '',
    symbol: '',
    description: '',
    imageUrl: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: ''
  });

  const [launchSettings, setLaunchSettings] = useState<LaunchSettings>({
    pumpfunInitialBuy: 0.1,
    bundleBuyAmount: 0.1,
    slippage: 10,
    priorityFee: 0.0005,
    useJitoBundle: false,
    jitoTip: 0.01
  });

  // Bundle state
  const [bundles, setBundles] = useState<any[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<any>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [bundlePassword, setBundlePassword] = useState('');
  const [showBundlePasswordModal, setShowBundlePasswordModal] = useState(false);
  const [bundleBuyProgress, setBundleBuyProgress] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
    status: string;
  }>({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });

  // UI state
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [step, setStep] = useState<'form' | 'creating' | 'success'>('form');
  const [createdTokenAddress, setCreatedTokenAddress] = useState<string>('');
  const [txSignature, setTxSignature] = useState<string>('');

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // Load bundles when wallet connects
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    }
  }, [isConnected, publicKey]);

  const loadUserBundles = async () => {
    if (!publicKey) return;
    try {
      const userBundles = await bundlerService.getUserBundles(publicKey.toString());
      setBundles(userBundles);
      
      // Check if there's an active unlocked bundle
      const currentBundle = bundlerService.getCurrentBundle();
      if (currentBundle && !bundlerService.getIsLocked()) {
        setSelectedBundle(currentBundle);
        setIsUnlocked(true);
      }
    } catch (error) {
      console.error('Failed to load bundles:', error);
    }
  };

  const unlockSelectedBundle = async () => {
    if (!selectedBundle || !bundlePassword) return;
    
    try {
      const unlockedBundle = await bundlerService.unlockBundle(
        selectedBundle.id, 
        bundlePassword
      );
      setIsUnlocked(true);
      setShowBundlePasswordModal(false);
      setBundlePassword('');
      console.log(`✅ Bundle "${unlockedBundle.name}" unlocked successfully!`);
    } catch (error: any) {
      alert(`Failed to unlock bundle: ${error.message}`);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      alert('Only PNG, JPG, GIF, WEBP are allowed');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert(`File too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${apiEndpoints.uploadLogo}?folder=pumpfun-tokens&prefix=pump`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      
      const data = await response.json();
      const imageUrl = data.secure_url || data.url || data.imageUrl;
      
      if (!imageUrl) {
        throw new Error('No image URL in response');
      }
      
      setFormData(prev => ({ ...prev, imageUrl }));
      console.log('✅ Image uploaded successfully:', imageUrl);
      
    } catch (error: any) {
      console.error('Upload error:', error);
      
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setFormData(prev => ({ ...prev, imageUrl: base64 }));
          console.log('📦 Using base64 fallback');
        };
        reader.readAsDataURL(file);
      } catch (fallbackError) {
        alert('Failed to upload image. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    
    if (!formData.name || formData.name.length > 50) {
      errors.push('Token name is required (max 50 characters)');
    }
    if (!formData.symbol || formData.symbol.length > 10) {
      errors.push('Token symbol is required (max 10 characters)');
    }
    if (!formData.description || formData.description.length > 500) {
      errors.push('Description is required (max 500 characters)');
    }
    if (!formData.imageUrl) {
      errors.push('Token logo is required');
    }
    if (!isConnected || !publicKey) {
      errors.push('Please connect your wallet');
    }
    
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return false;
    }
    
    return true;
  };

  const handleLaunch = async () => {
    if (!validateForm() || !publicKey) return;

    setLoading(true);
    setStep('creating');
    setBundleBuyProgress({
      total: 0,
      completed: 0,
      successful: 0,
      failed: 0,
      status: 'Creating token...'
    });

    try {
      const provider = (window as any).solana;
      if (!provider?.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const tokenData = {
        name: formData.name,
        symbol: formData.symbol,
        description: formData.description,
        image: formData.imageUrl,
        website: formData.website || undefined,
        twitter: formData.twitter || undefined,
        telegram: formData.telegram || undefined,
        discord: formData.discord || undefined,
        showName: true,
        buyAmount: launchSettings.pumpfunInitialBuy,  // Main wallet buy
        slippage: launchSettings.slippage,
        priorityFee: launchSettings.priorityFee,
      };

      let txSignature = '';
      let mintAddress = '';

      if (launchSettings.useJitoBundle) {
        throw new Error(
          'Jito bundle flow is not implemented yet. Disable MEV protection or implement Jito submission and wallet co-signing.'
        );
      }

      const result = await pumpfunService.createToken(tokenData, publicKey.toString());

      if (!result.success) {
        throw new Error(result.error || 'Failed to create token');
      }
      if (!result.signature) {
        throw new Error('No transaction returned from create token');
      }

      if (!provider.isConnected) {
        await provider.connect();
      }

      try {
        const signature = await pumpfunService.signAndSendTransaction(
          result.signature,
          async (tx) => {
            console.log('Requesting wallet signature...');
            return await provider.signTransaction(tx);
          }
        );

        if (!signature) {
          throw new Error('Wallet did not return a transaction signature');
        }

        txSignature = signature;
        mintAddress = result.mint || '';

        if (!mintAddress && txSignature) {
          await new Promise((r) => setTimeout(r, 3000));
          mintAddress =
            (await pumpfunService.getMintFromSignature(
              txSignature,
              publicKey.toString()
            )) || '';
        }
      } catch (signError) {
        const msg = signError instanceof Error ? signError.message : String(signError);
        console.error('Failed to sign transaction:', signError);
        throw new Error(`Failed to sign transaction: ${msg}`);
      }

      if (!txSignature) {
        throw new Error('No transaction signature produced. Creation aborted.');
      }

      if (!mintAddress && txSignature) {
        console.log('Extracting mint address from transaction...');
        mintAddress = (await pumpfunService.getMintFromSignature(txSignature)) || '';
      }

      console.log('🎉 Token created!', { txSignature, mintAddress });

      // Execute bundle auto-buys with separate amount
      if (selectedBundle && isUnlocked && mintAddress) {
        console.log('🎯 Starting bundle buys for token:', mintAddress);

        const eligibleWallets = selectedBundle.wallets.filter(
          (w: any) => w.balance >= (launchSettings.bundleBuyAmount + 0.002)
        );

        setBundleBuyProgress({
          total: eligibleWallets.length,
          completed: 0,
          successful: 0,
          failed: 0,
          status: 'Executing bundle purchases...',
        });

       const bundleResult = await bundlerService.executePumpFunBundlePurchase(
        mintAddress,
        launchSettings.bundleBuyAmount,  // Use bundle-specific amount
        launchSettings.slippage,
          (progress, status) => {
            const match = status.match(/(\d+) success, (\d+) failed/);
            if (match) {
              setBundleBuyProgress({
                total: eligibleWallets.length,
                completed: parseInt(match[1]) + parseInt(match[2]),
                successful: parseInt(match[1]),
                failed: parseInt(match[2]),
                status,
              });
            } else {
              setBundleBuyProgress((prev) => ({ ...prev, status }));
            }
          }
        );

        if (bundleResult.success) {
          console.log(`✅ Bundle buys complete: ${bundleResult.successful} successful`);
          console.log('Transaction signatures:', bundleResult.transactions);

          setBundleBuyProgress((prev) => ({
            ...prev,
            completed: prev.total,
            successful: bundleResult.successful,
            failed: bundleResult.failed,
            status: `Bundle purchases complete! ${bundleResult.successful}/${prev.total} successful`,
          }));
        } else {
          console.error('Bundle buy failed:', bundleResult.error);
        }
      }

      setTxSignature(txSignature);
      setCreatedTokenAddress(mintAddress || 'Check transaction for token address');
      setStep('success');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Launch failed:', error);
      alert(`Failed to launch token: ${msg}`);
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      symbol: '',
      description: '',
      imageUrl: '',
      website: '',
      twitter: '',
      telegram: '',
      discord: ''
    });
    setLaunchSettings({
      pumpfunInitialBuy: 0.1,
      bundleBuyAmount: 0.1,
      slippage: 10,
      priorityFee: 0.0005,
      useJitoBundle: false,
      jitoTip: 0.01
    });
    setStep('form');
    setCreatedTokenAddress('');
    setTxSignature('');
    setBundleBuyProgress({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });
  };

  // Success Screen
  if (step === 'success') {
    return (
       <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <TrendingUp className="w-10 h-10 text-green-400" />
          </div>
          
          <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            Token Launched on Pump.fun!
          </h2>
          
          <p className="text-gray-400 mb-8">
            Your token is now live with a bonding curve
          </p>

          {createdTokenAddress && createdTokenAddress !== 'Check transaction for token address' && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-400 mb-2">Token Address</div>
              <div className="font-mono text-sm break-all text-green-400">
                {createdTokenAddress}
              </div>
            </div>
          )}

          {txSignature && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-2">Transaction</div>
              <div className="font-mono text-xs break-all">{txSignature}</div>
            </div>
          )}

          {bundleBuyProgress.total > 0 && (
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4 mb-6">
              <div className="text-sm font-medium text-purple-400 mb-2">Bundle Purchase Results</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-gray-900/50 rounded p-2">
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="font-semibold">{bundleBuyProgress.total}</div>
                </div>
                <div className="bg-green-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Successful</div>
                  <div className="font-semibold text-green-400">{bundleBuyProgress.successful}</div>
                </div>
                <div className="bg-red-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Failed</div>
                  <div className="font-semibold text-red-400">{bundleBuyProgress.failed}</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {createdTokenAddress && createdTokenAddress !== 'Check transaction for token address' && (
              <a
                href={`https://pump.fun/${createdTokenAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>View on Pump.fun</span>
              </a>
            )}
            
            <a
              href={`https://solscan.io/tx/${txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Transaction</span>
            </a>
            
            <button
              onClick={resetForm}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Launch Another Token
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Creating Screen
  if (step === 'creating') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-6 text-green-400" />
          <h2 className="text-2xl font-bold mb-3">Launching on Pump.fun...</h2>
          <p className="text-gray-400 mb-2">{bundleBuyProgress.status || 'Creating your token with bonding curve'}</p>
          
          {launchSettings.useJitoBundle && (
            <div className="mt-4 inline-flex items-center space-x-2 bg-purple-900/20 border border-purple-700/50 rounded-lg px-4 py-2">
              <Shield className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400">
                MEV Protected with Jito Bundle
              </span>
            </div>
          )}
          
          {bundleBuyProgress.total > 0 && (
            <div className="mt-6 max-w-md mx-auto">
              <div className="text-sm font-medium mb-2">Bundle Buy Progress</div>
              <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bundleBuyProgress.completed / bundleBuyProgress.total) * 100}%` }}
                />
              </div>
              <div className="text-xs text-gray-400">
                {bundleBuyProgress.completed}/{bundleBuyProgress.total} wallets • 
                <span className="text-green-400"> {bundleBuyProgress.successful} successful</span> • 
                <span className="text-red-400"> {bundleBuyProgress.failed} failed</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-600/20 rounded-lg">
            <TrendingUp className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Launch on Pump.fun</h2>
            <p className="text-sm text-gray-400">Fair launch with bonding curve</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-xs text-gray-500">Platform Fee</div>
          <div className="text-lg font-semibold text-green-400">0.02 SOL</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <TrendingUp className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-green-400 mb-1">Pump.fun Launch</div>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>✓ Fair launch with no team allocation</li>
              <li>✓ Bonding curve pricing mechanism</li>
              <li>✓ Auto-migration to Raydium at $69k market cap</li>
              <li>✓ Built-in anti-rug protection</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Token Name */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 50) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
            placeholder="My Awesome Token"
            maxLength={50}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.name.length}/50 characters
          </div>
        </div>

        {/* Token Symbol */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Symbol <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.symbol}
            onChange={(e) => setFormData({ ...formData, symbol: e.target.value.slice(0, 10).toUpperCase() })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
            placeholder="TOKEN"
            maxLength={10}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.symbol.length}/10 characters
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, 500) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
            placeholder="Describe your token and its purpose..."
            rows={3}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.description.length}/500 characters
          </div>
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Logo <span className="text-red-500">*</span>
          </label>
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) await handleFileUpload(file);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full min-h-[140px] rounded-xl border-2 border-dashed transition-all cursor-pointer ${
              isDragging 
                ? 'border-green-400 bg-green-500/5' 
                : 'border-gray-700 bg-gray-900/30 hover:border-green-500/50'
            } flex items-center justify-center p-6`}
          >
            {!formData.imageUrl && !uploadingLogo && (
              <div className="text-center">
                <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">
                  Drop your logo here or click to upload
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF or WEBP (max 5MB)
                </p>
              </div>
            )}

            {uploadingLogo && (
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-green-400" />
                <span className="text-sm">Uploading to IPFS...</span>
              </div>
            )}

            {formData.imageUrl && !uploadingLogo && (
              <div className="flex items-center gap-4">
                <img
                  src={formData.imageUrl}
                  alt="Token Logo"
                  className="w-20 h-20 rounded-lg object-cover border-2 border-green-500/30"
                />
                <div className="text-left">
                  <p className="text-sm text-gray-400 mb-2">Logo uploaded</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await handleFileUpload(file);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </div>

        {/* Social Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Twitter <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.twitter}
              onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              placeholder="@username or https://twitter.com/..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Telegram <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              placeholder="t.me/groupname"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Website <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              placeholder="https://..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Discord <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.discord}
              onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
              placeholder="discord.gg/invite"
            />
          </div>
        </div>

        {/* Bundle Selection */}
        <div className="border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium flex items-center space-x-2">
              <Package className="w-4 h-4 text-purple-400" />
              <span>Bundle Auto-Buy (Optional)</span>
            </h3>
            {selectedBundle && (
              <span className={`px-2 py-1 text-xs rounded-full ${
                isUnlocked 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
              </span>
            )}
          </div>
          
          {bundles.length > 0 ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bundles.map((bundle) => {
                  const eligibleWallets = bundle.wallets.filter(
                    (w: any) => w.balance >= (launchSettings.bundleBuyAmount + 0.002)
                  );
                  
                  return (
                    <button
                      key={bundle.id}
                      type="button"
                      onClick={() => {
                        setSelectedBundle(bundle);
                        setIsUnlocked(false);
                      }}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedBundle?.id === bundle.id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium">{bundle.name}</div>
                        {selectedBundle?.id === bundle.id && (
                          <Check className="w-4 h-4 text-purple-400" />
                        )}
                      </div>
                      <div className="text-xs text-gray-400 space-y-1">
                        <div>{bundle.wallets.length} wallets • {bundle.totalBalance.toFixed(3)} SOL</div>
                        <div className={eligibleWallets.length > 0 ? 'text-green-400' : 'text-red-400'}>
                          {eligibleWallets.length} can afford {launchSettings.bundleBuyAmount} SOL + fees
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {selectedBundle && !isUnlocked && (
                <button
                  type="button"
                  onClick={() => setShowBundlePasswordModal(true)}
                  className="w-full px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-all"
                >
                  Unlock Bundle for Auto-Buy
                </button>
              )}
              
              {selectedBundle && isUnlocked && (
                <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-green-400">Bundle Ready</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {selectedBundle.wallets.filter((w: any) => 
                          w.balance >= (launchSettings.bundleBuyAmount + 0.002)
                        ).length} wallets will automatically buy {launchSettings.bundleBuyAmount} SOL each after token creation
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-400">
              No bundles available. Create a bundle in Advanced Tools to enable auto-buying.
            </div>
          )}
        </div>

        {/* Launch Settings */}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="font-medium flex items-center space-x-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Advanced Launch Settings</span>
            </span>
            <span className="text-gray-400">
              {showAdvanced ? '−' : '+'}
            </span>
          </button>
          
          {showAdvanced && (
            <div className="mt-4 space-y-4">
              {/* Pump.fun Initial Buy */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Initial Buy from Main Wallet (SOL)
                </label>
                <input
                  type="number"
                  value={launchSettings.pumpfunInitialBuy}
                  onChange={(e) => setLaunchSettings({
                    ...launchSettings,
                    pumpfunInitialBuy: Math.max(0, parseFloat(e.target.value) || 0)
                  })}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
                  placeholder="0.1"
                  min="0"
                  step="0.01"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Amount your main wallet buys immediately on pump.fun
                </div>
              </div>

              {/* Bundle Buy Amount - Only show if bundle selected */}
              {selectedBundle && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Buy Amount per Bundle Wallet (SOL)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.bundleBuyAmount}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      bundleBuyAmount: Math.max(0, parseFloat(e.target.value) || 0)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
                    placeholder="0.1"
                    min="0"
                    step="0.01"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Each bundle wallet will buy this amount after token creation
                  </div>
                </div>
              )}

              {/* Trading Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Slippage (%)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.slippage}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      slippage: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
                    min="1"
                    max="50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Priority Fee (SOL)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.priorityFee}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      priorityFee: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
                    min="0"
                    step="0.0001"
                  />
                </div>
              </div>

              {/* Jito Bundle Option */}
              <div className="bg-purple-900/10 border border-purple-700/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="jito-bundle"
                    checked={launchSettings.useJitoBundle}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      useJitoBundle: e.target.checked
                    })}
                    className="mt-1 w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="jito-bundle" className="flex-1 cursor-pointer">
                    <div className="font-medium flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span>MEV Protection (Jito Bundle)</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Protect against sandwich attacks and ensure transaction success
                    </p>
                  </label>
                </div>
                
                {launchSettings.useJitoBundle && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium mb-2">
                      Jito Tip (SOL)
                    </label>
                    <input
                      type="number"
                      value={launchSettings.jitoTip}
                      onChange={(e) => setLaunchSettings({
                        ...launchSettings,
                        jitoTip: Number(e.target.value)
                      })}
                      className="w-full px-3 py-1.5 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                      min="0.001"
                      step="0.001"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Higher tips = higher priority
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-900/50 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <DollarSign className="w-4 h-4 text-green-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-green-400 mb-3">Launch Cost Breakdown</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Platform Fee:</span>
                  <span className="font-mono">0.02 SOL</span>
                </div>
                {launchSettings.pumpfunInitialBuy > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Initial Buy (Main Wallet):</span>
                    <span className="font-mono">{launchSettings.pumpfunInitialBuy.toFixed(3)} SOL</span>
                  </div>
                )}
                {selectedBundle && isUnlocked && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      Bundle Buy ({selectedBundle.wallets.filter((w: any) => 
                        w.balance >= (launchSettings.bundleBuyAmount + 0.002)
                      ).length} wallets):
                    </span>
                    <span className="font-mono">
                      {(launchSettings.bundleBuyAmount * selectedBundle.wallets.filter((w: any) => 
                        w.balance >= (launchSettings.bundleBuyAmount + 0.002)
                      ).length).toFixed(3)} SOL
                    </span>
                  </div>
                )}
                {launchSettings.useJitoBundle && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Jito Tip:</span>
                    <span className="font-mono">{launchSettings.jitoTip.toFixed(3)} SOL</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2">
                  <div className="flex justify-between font-medium">
                    <span>Total from Main Wallet:</span>
                    <span className="text-green-400 font-mono">
                      {(
                        0.02 + 
                        launchSettings.pumpfunInitialBuy + 
                        (launchSettings.useJitoBundle ? launchSettings.jitoTip : 0)
                      ).toFixed(3)} SOL
                    </span>
                  </div>
                  {selectedBundle && isUnlocked && (
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>+ Bundle wallets will spend their own SOL</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Launch Button */}
        <button
          onClick={handleLaunch}
          disabled={loading || !isConnected}
          className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl font-semibold text-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg hover:shadow-green-500/20"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Launching...</span>
            </>
          ) : (
            <>
              <Rocket className="w-5 h-5" />
              <span>Launch on Pump.fun</span>
              <TrendingUp className="w-5 h-5" />
            </>
          )}
        </button>

        {!isConnected && (
          <p className="text-center text-sm text-yellow-400 mt-3 flex items-center justify-center space-x-2">
            <AlertCircle className="w-4 h-4" />
            <span>Please connect your wallet to launch</span>
          </p>
        )}
      </div>

      {/* Bundle Password Modal */}
      <Modal 
        isOpen={showBundlePasswordModal} 
        onClose={() => {
          setShowBundlePasswordModal(false);
          setBundlePassword('');
        }}
      >
        <h3 className="text-xl font-bold mb-4">Unlock Bundle</h3>
        <p className="text-sm text-gray-400 mb-4">
          Enter password to unlock "{selectedBundle?.name}" for auto-buying
        </p>
        
        <input
          type="password"
          value={bundlePassword}
          onChange={(e) => setBundlePassword(e.target.value)}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-purple-500"
          placeholder="Bundle password"
          onKeyPress={(e) => e.key === 'Enter' && unlockSelectedBundle()}
          autoFocus
        />
        
        <div className="flex gap-3">
          <button
            onClick={unlockSelectedBundle}
            className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-colors"
          >
            Unlock
          </button>
          <button
            onClick={() => {
              setShowBundlePasswordModal(false);
              setBundlePassword('');
            }}
            className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PumpfunCreationForm;